import axios, { AxiosResponse } from 'axios';
import { BusinessNameValidationRequest, BusinessNameValidationResponse, AvailabilityStatus } from '../types';
import { ENDPOINTS } from '../config/endpoints';

// Use centralized endpoint configuration
const BUSINESS_NAME_CHECK_ENDPOINT = ENDPOINTS.CHECK_BUSINESS_NAME;

// Timeout for API calls (10 seconds)
const API_TIMEOUT = 10000;

// Create axios instance with default config
const businessNameApiClient = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Checks if a business name is available in the Organisation API
 * @param businessName - The business name to check (e.g., "Example Business Ltd")
 * @returns Promise with business name validation response
 */
/**
 * May the customer move past the name check?
 *
 * Yes if the name is available, and yes if we could not check it at all - a
 * failed check is not a taken name, and a genuine duplicate is still caught by
 * the onboarding pipeline. Only a confirmed conflict stops them.
 *
 * Every gate in the signup flow uses this. They used to spell the rule out
 * separately, and two of them - the platform-setup step and the submit
 * handler - still tested `isAvailable !== true`, so a customer whose check
 * failed was shown the amber "we couldn't check" message and then blocked
 * anyway, the submit path telling them to "choose an available subdomain".
 */
export const nameCheckAllowsProgress = (validation: {
  isAvailable: boolean | null;
  status?: AvailabilityStatus | null;
}): boolean => validation.isAvailable === true || validation.status === 'check_failed';

export const checkBusinessNameAvailability = async (businessName: string): Promise<BusinessNameValidationResponse> => {
  try {
    // Clean and validate input
    const cleanBusinessName = businessName.trim();
    
    console.log('=== Business Name Availability Check ===');
    console.log('Endpoint:', BUSINESS_NAME_CHECK_ENDPOINT);
    console.log('Business name:', cleanBusinessName);
    
    if (!cleanBusinessName) {
      throw new Error('Business name is required');
    }

    // Basic format validation (client-side)
    if (cleanBusinessName.length < 2 || cleanBusinessName.length > 200) {
      return {
        available: false,
        businessName: cleanBusinessName,
        normalizedName: cleanBusinessName.toLowerCase(),
        error: 'Invalid business name length',
        message: 'Business name must be between 2-200 characters long'
      };
    }

    // Check if name contains at least one alphanumeric character
    if (!/[a-zA-Z0-9]/.test(cleanBusinessName)) {
      return {
        available: false,
        businessName: cleanBusinessName,
        normalizedName: cleanBusinessName.toLowerCase(),
        error: 'Invalid business name format',
        message: 'Business name must contain at least one letter or number'
      };
    }

    const requestData: BusinessNameValidationRequest = {
      businessName: cleanBusinessName
    };

    console.log('Making business name check request to:', BUSINESS_NAME_CHECK_ENDPOINT);
    console.log('Request data:', requestData);

    const response: AxiosResponse<BusinessNameValidationResponse> = await businessNameApiClient.post(
      BUSINESS_NAME_CHECK_ENDPOINT,
      requestData
    );

    console.log('Business name check response:', response.data);

    // Older backend deployments do not send `status`; derive it so callers can
    // rely on the field being present.
    return {
      ...response.data,
      status: response.data.status ?? (response.data.available ? 'available' : 'taken'),
    };

  } catch (error) {
    console.error('Business name availability check error:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      isAxiosError: axios.isAxiosError(error),
      status: axios.isAxiosError(error) ? error.response?.status : 'N/A',
      statusText: axios.isAxiosError(error) ? error.response?.statusText : 'N/A',
      data: axios.isAxiosError(error) ? error.response?.data : 'N/A'
    });

    // Every branch below must be explicit about WHICH kind of failure it is.
    //
    // A failed check is not a taken name. Returning `available:false` alone made
    // the two indistinguishable, and the form rendered both as "This business
    // name is already registered" - turning a transient outage into a hard stop
    // for a customer whose chosen name was in fact free.
    const name = businessName.trim();
    const checkFailed = (message: string): BusinessNameValidationResponse => ({
      available: false,
      status: 'check_failed',
      businessName: name,
      normalizedName: name.toLowerCase(),
      error: 'availability_check_unavailable',
      message,
    });

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        // A real axios timeout: an AxiosError with no response, which would
        // otherwise fall to `default:` and blame the customer's connection.
        return checkFailed('The name check timed out. Please try again.');
      }
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;

      switch (status) {
        case 400:
          // A genuine rejection of the input, not a failure to check it.
          return {
            available: false,
            businessName: name,
            normalizedName: name.toLowerCase(),
            error: 'Invalid request format',
            message: serverMessage || 'Please check your business name format',
          };

        case 409:
          // Conflict means the business name really is already registered.
          return {
            available: false,
            status: 'taken',
            businessName: name,
            normalizedName: name.toLowerCase(),
            error: 'Business name exists',
            message: 'This business name is already registered',
          };

        case 429:
          return checkFailed('Too many requests. Please try again in a moment.');

        case 503:
          // The backend telling us plainly that it could not complete the check.
          return checkFailed(
            serverMessage || "We couldn't check this name right now. Please try again."
          );

        case 500:
          return checkFailed('The name check service is temporarily unavailable.');

        default:
          // No response at all: network failure, CORS rejection, DNS failure.
          return checkFailed(
            "We couldn't reach the name check service. Please check your connection and try again."
          );
      }
    }

    if (error instanceof Error && error.message.includes('timeout')) {
      return checkFailed('The name check timed out. Please try again.');
    }

    return checkFailed("We couldn't check this name right now. Please try again.");
  }
};

/**
 * Debounced business name availability checking function
 * @param businessName - The business name to check
 * @param delay - Delay in milliseconds (default: 800ms)
 * @returns Promise with validation response
 */
export const debouncedCheckBusinessName = (() => {
  let timeoutId: NodeJS.Timeout;
  
  return (businessName: string, delay: number = 800): Promise<BusinessNameValidationResponse> => {
    return new Promise((resolve) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const result = await checkBusinessNameAvailability(businessName);
        resolve(result);
      }, delay);
    });
  };
})();

/**
 * Test connection to business name check service
 * @returns Promise indicating if the service is available
 */
export const testBusinessNameService = async (): Promise<boolean> => {
  try {
    // Use a test business name to check service availability
    const response = await businessNameApiClient.post(
      BUSINESS_NAME_CHECK_ENDPOINT.replace('/CheckBusinessNameAvailability', '/healthcheck'),
      {},
      { timeout: 5000 }
    );
    return response.status === 200;
  } catch (error) {
    console.warn('Business name service health check failed:', error);
    return false;
  }
};

const businessNameValidationService = {
  checkBusinessNameAvailability,
  debouncedCheckBusinessName,
  testBusinessNameService
};

export default businessNameValidationService;
